import { z } from 'zod'

/**
 * Today's date as `YYYY-MM-DD` in the user's **local** timezone.
 * Used for comparing date-only strings from the form/pickers so validation matches what users see
 * (avoids rejecting "today" when UTC calendar day differs from local).
 */
export function todayDateOnlyLocal(): string {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function parseOptionalNumber(val: unknown): number | null | undefined {
  if (val === '' || val === undefined) return undefined
  if (val === null) return null
  if (typeof val === 'number' && Number.isFinite(val)) return val
  const n = Number(val)
  return Number.isFinite(n) ? n : undefined
}

/** Step-1 form values (controlled before API) */
export const campaignStep1FormSchema = z.object({
  name: z
    .string()
    .max(100, 'Campaign name must not exceed 100 characters'),
  researchGoal: z
    .string()
    .max(2500, 'Research goal must not exceed 2500 characters.'),
  budget: z
    .union([z.number(), z.string(), z.null(), z.undefined()])
    .transform(parseOptionalNumber)
    .pipe(z.number().nonnegative().nullable().optional()),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  targetAudience: z.string(),
  /** Optional company link — empty string means unlinked */
  companyId: z.string().optional(),
})

export type CampaignStep1FormValues = z.input<typeof campaignStep1FormSchema>

/** Submit-time validation (Next) — empty-field copy from Figma 4255:3815 */
export const campaignStep1SubmitSchema = campaignStep1FormSchema
  .extend({
    name: z
      .string()
      .trim()
      .min(1, 'Campaign name is required')
      .min(10, 'Campaign name must be at least 10 characters long')
      .max(100, 'Campaign name must not exceed 100 characters'),
    researchGoal: z
      .string()
      .trim()
      .min(1, 'Research goal is required')
      .min(100, 'Research goal must be at least 100 characters long.')
      .max(2500, 'Research goal must not exceed 2500 characters.'),
    startDate: z.string().min(1, 'Please select a start date'),
    endDate: z.string().min(1, 'Please select an end date'),
    targetAudience: z.string().trim().min(1, 'Target audience is required'),
  })
  .superRefine((data, ctx) => {
    const today = todayDateOnlyLocal()

    if (data.startDate && data.startDate < today) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Dates cannot be in the past',
        path: ['startDate'],
      })
    }

    if (data.endDate && data.endDate < today) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Dates cannot be in the past',
        path: ['endDate'],
      })
    }

    if (data.startDate && data.endDate && data.endDate <= data.startDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'End date must be after start date',
        path: ['endDate'],
      })
    }
  })

export type CampaignStep1SubmitValues = z.infer<typeof campaignStep1SubmitSchema>
